import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import type { ThunkDispatch } from 'redux-thunk'
import type { AnyAction } from 'redux'
import { createFileUploads, removeAssetUploads, updateAssetUploadFile, renameUploadedAsset, deleteWatchedFile, checkAssetFilenamesMatches } from 'src/actions/assetUploads'
import FilenamesMatchResult from '@uploads/models/FilenamesMatchResult'
import { ASSET_ALL_EXTENSIONS } from 'src/entities/asset'
import { useApi } from 'src/new_arch/providers/ApiProvider'
import { PROJECTS_URL } from 'src/config/urls'

type FSDirectoryHandle = FileSystemDirectoryHandle & {
	values(): AsyncIterableIterator<FileSystemFileHandle | FileSystemDirectoryHandle>
}
type WindowWithFSA = {
	showDirectoryPicker( options?: { mode?: 'read' | 'readwrite' } ): Promise<FSDirectoryHandle>
}

export type SyncedFile = {
	filename: string
	detectedAt: Date
}

type UseFolderSyncParams = {
	projectID: number
	folderID: number | null
}

type UseFolderSyncResult = {
	isBrowserSupported: boolean
	isWatching: boolean
	folderName: string | null
	syncedFiles: SyncedFile[]
	startWatching: () => Promise<boolean>
	stopWatching: () => void
}

type Fingerprint = string

type TrackedEntry = {
	filename: string
	uploadID: string | number
	size: number
	lastModified: number
	pendingRename?: string
}

const POLL_INTERVAL_MS = 15_000
const STABILITY_CHECK_DELAY_MS = 500

const isFolderSyncSupported = () => 'showDirectoryPicker' in window

const isAllowedExtension = ( filename: string ) => {
	const ext = filename.split( '.' ).pop()?.toLowerCase()
	return !!ext && ASSET_ALL_EXTENSIONS.includes( ext )
}

const fingerprint = ( size: number, lastModified: number ): Fingerprint => `${size}:${lastModified}`

const collectHandles = async (
	dirHandle: FSDirectoryHandle
): Promise<FileSystemFileHandle[]> => {
	const iter = dirHandle.values()
	const drain = async ( acc: FileSystemFileHandle[] ): Promise<FileSystemFileHandle[]> => {
		const { done, value } = await iter.next()
		if ( done ) return acc
		if ( value.kind === 'file' ) acc.push( value as FileSystemFileHandle )
		return drain( acc )
	}
	return drain( [] )
}

const isFileStable = async ( handle: FileSystemFileHandle, knownSize: number ): Promise<boolean> => {
	await new Promise( resolve => setTimeout( resolve, STABILITY_CHECK_DELAY_MS ) )
	return ( await handle.getFile() ).size === knownSize
}

const useFolderSync = ( { projectID, folderID }: UseFolderSyncParams ): UseFolderSyncResult => {
	const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>()
	const api = useApi()
	const [ isWatching, setIsWatching ] = useState( false )
	const [ folderName, setFolderName ] = useState<string | null>( null )
	const [ syncedFiles, setSyncedFiles ] = useState<SyncedFile[]>( [] )

	const dirHandleRef = useRef<FSDirectoryHandle | null>( null )
	const trackedRef = useRef<Map<string | number, TrackedEntry>>( new Map() )
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>( null )
	const seenFilenamesRef = useRef<Set<string>>( new Set() )

	const isBrowserSupported = isFolderSyncSupported()

	const scanAndUpload = useCallback( async () => {
		const dirHandle = dirHandleRef.current
		if ( !dirHandle ) return

		const allHandles = await collectHandles( dirHandle )
		const allowedHandles = allHandles.filter( h => isAllowedExtension( h.name ) )

		const trackedFilenames = new Set(
			Array.from( trackedRef.current.values() ).map( e => e.filename )
		)
		const handlesToFetch = allowedHandles.filter(
			h => trackedFilenames.has( h.name ) || !seenFilenamesRef.current.has( h.name )
		)
		const fetchedFiles = await Promise.all( handlesToFetch.map( h => h.getFile() ) )

		const currentByFilename = new Map<string, File>()
		const currentByFingerprint = new Map<Fingerprint, File>()
		fetchedFiles.forEach( ( f ) => {
			currentByFilename.set( f.name, f )
			currentByFingerprint.set( fingerprint( f.size, f.lastModified ), f )
		} )

		const unmatchedFilenames = new Set<string>( allowedHandles.map( h => h.name ) )
		const usedFingerprints = new Set<Fingerprint>()

		Array.from( trackedRef.current.entries() ).forEach( ( [ uploadID, entry ] ) => {
			const fp = fingerprint( entry.size, entry.lastModified )
			const byName = currentByFilename.get( entry.filename )

			if ( byName ) {
				unmatchedFilenames.delete( entry.filename )

				if ( byName.lastModified !== entry.lastModified ) {
					trackedRef.current.set( uploadID, {
						...entry, size: byName.size, lastModified: byName.lastModified
					} )
				}
			} else {
				const byFP = currentByFingerprint.get( fp )
				const isRename = byFP && unmatchedFilenames.has( byFP.name ) && !usedFingerprints.has( fp )

				if ( isRename ) {
					const newFilename = byFP.name
					usedFingerprints.add( fp )
					unmatchedFilenames.delete( newFilename )
					trackedRef.current.set( uploadID, {
						...entry, filename: newFilename, pendingRename: newFilename
					} )
					dispatch( updateAssetUploadFile( uploadID, byFP ) )
					setSyncedFiles( prev => prev.map(
						f => ( f.filename === entry.filename ? { ...f, filename: newFilename } : f )
					) )
				} else {
					trackedRef.current.delete( uploadID )
					dispatch( deleteWatchedFile( uploadID ) )
					setSyncedFiles( prev => prev.filter( f => f.filename !== entry.filename ) )
				}
			}
		} )

		const newHandles = allowedHandles.filter(
			h => unmatchedFilenames.has( h.name ) && !seenFilenamesRef.current.has( h.name )
		)
		const stableNewHandles = await Promise.all(
			newHandles.map( async ( handle ) => {
				const file = currentByFilename.get( handle.name )!
				return { handle, file, stable: await isFileStable( handle, file.size ) }
			} )
		).then( results => results.filter( r => r.stable ) )

		if ( stableNewHandles.length > 0 ) {
			const files = stableNewHandles.map( e => e.file )

			const matchDispatch = dispatch( checkAssetFilenamesMatches( { projectID, folderID, files } ) )
			const matchAction = await ( matchDispatch as Promise<{ payload: FilenamesMatchResult }> )
			const newRounds = ( matchAction?.payload?.exactMatches ?? [] ).map(
				m => ( { filename: m.filename, assetID: m.match.id } )
			)

			const action = dispatch(
				createFileUploads( { projectID, folderID, parentUploadID: undefined, files, newRounds } )
			)
			const payload: Array<{ id: string | number }> = action?.payload ?? []

			stableNewHandles.forEach( ( { file }, index ) => {
				const uploadID = payload[ index ]?.id
				if ( uploadID == null ) return
				trackedRef.current.set( uploadID, {
					filename: file.name,
					uploadID,
					size: file.size,
					lastModified: file.lastModified
				} )
				setSyncedFiles( prev => [ ...prev, { filename: file.name, detectedAt: new Date() } ] )
			} )

			const uploadedFilenames = stableNewHandles.map( e => e.file.name )
			const currentFolderName = dirHandleRef.current?.name
			if ( currentFolderName ) {
				api.post(
					`${PROJECTS_URL}/${projectID}/folder_sync/register`,
					{ folder_name: currentFolderName, folder_id: folderID ?? 0, filenames: uploadedFilenames }
				).then( () => {
					uploadedFilenames.forEach( name => seenFilenamesRef.current.add( name ) )
				} ).catch( () => { /* non-critical — delta sync degrades gracefully */ } )
			}
		}

		Array.from( trackedRef.current.entries() )
			.filter( ( [ , entry ] ) => !!entry.pendingRename )
			.forEach( ( [ uploadID, entry ] ) => {
				( dispatch( renameUploadedAsset( uploadID, entry.pendingRename! ) ) as Promise<boolean> )
					.then( ( renamed ) => {
						if ( !renamed ) return
						const current = trackedRef.current.get( uploadID )
						if ( current ) trackedRef.current.set( uploadID, { ...current, pendingRename: undefined } )
					} )
			} )
	}, [ dispatch, projectID, folderID ] )

	const clearTrackedUploads = useCallback( () => {
		if ( trackedRef.current.size === 0 ) return
		const ids = [ ...trackedRef.current.values() ].map( e => e.uploadID )
		dispatch( removeAssetUploads( ids ) )
		trackedRef.current = new Map()
	}, [ dispatch ] )

	const stopWatching = useCallback( () => {
		clearTrackedUploads()
		if ( intervalRef.current ) {
			clearInterval( intervalRef.current )
			intervalRef.current = null
		}
		dirHandleRef.current = null
		seenFilenamesRef.current = new Set()
		setIsWatching( false )
		setFolderName( null )
		setSyncedFiles( [] )
	}, [ clearTrackedUploads ] )

	const startWatching = useCallback( async (): Promise<boolean> => {
		if ( !isFolderSyncSupported() ) return false

		try {
			clearTrackedUploads()

			const dirHandle = await ( window as unknown as WindowWithFSA ).showDirectoryPicker( { mode: 'read' } )
			dirHandleRef.current = dirHandle
			trackedRef.current = new Map()
			setSyncedFiles( [] )
			setFolderName( dirHandle.name )
			setIsWatching( true )

			const params = new URLSearchParams( {
				folder_name: dirHandle.name,
				folder_id: String( folderID ?? 0 )
			} )
			try {
				const response = await api.get( `${PROJECTS_URL}/${projectID}/folder_sync?${params}` )
				const { synced_filenames: alreadySynced = [] } = response as unknown as { synced_filenames: string[] }
				seenFilenamesRef.current = new Set( alreadySynced )
			} catch {
				seenFilenamesRef.current = new Set()
			}

			await scanAndUpload()
			intervalRef.current = setInterval( scanAndUpload, POLL_INTERVAL_MS )
			return true
		} catch ( err ) {
			if ( !( err instanceof DOMException && err.name === 'AbortError' ) ) {
				throw err
			}
			return false
		}
	}, [ scanAndUpload, clearTrackedUploads, api, projectID, folderID ] )

	useEffect( () => {
		if ( !isWatching ) return () => {}
		const handleBeforeUnload = ( e: BeforeUnloadEvent ) => {
			e.preventDefault()
			e.returnValue = ''
		}
		window.addEventListener( 'beforeunload', handleBeforeUnload )
		return () => window.removeEventListener( 'beforeunload', handleBeforeUnload )
	}, [ isWatching ] )

	useEffect( () => () => {
		if ( intervalRef.current ) clearInterval( intervalRef.current )
		if ( trackedRef.current.size > 0 ) {
			const ids = [ ...trackedRef.current.values() ].map( e => e.uploadID )
			dispatch( removeAssetUploads( ids ) )
		}
	}, [] )

	return {
		isBrowserSupported,
		isWatching,
		folderName,
		syncedFiles,
		startWatching,
		stopWatching
	}
}

export default useFolderSync
